import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * Guía en español (locale: 'es'): NIS2 en España y el estado de la
 * transposición nacional. Pieza local original (Anteproyecto de Ley de
 * Coordinación y Gobernanza de la Ciberseguridad, Centro Nacional de
 * Ciberseguridad, reparto de competencias español), no una traducción de la
 * guía inglesa sobre NIS2, que trata la directiva en sí y sigue accesible
 * como lectura complementaria a través de los enlaces relacionados.
 */
export function Nis2EspanaGuideBody() {
  return (
    <>
      <Lead>
        NIS2 es la directiva europea de ciberseguridad que España debía
        transponer antes del 17 de octubre de 2024. Esa fecha pasó, la ley
        española sigue en tramitación y la Comisión Europea ya ha reclamado
        dos veces. Pero cuidado: que la ley nacional se retrase no significa
        que puedas esperar. Los requisitos ya están fijados en la propia
        directiva. Esta guía explica en qué punto está la transposición
        española, a quién afectará, qué exigirá, y qué significa todo esto,
        desde hoy, para el logging y la monitorización de tu organización.
      </Lead>

      <GuideSection id="que-es-nis2" title="De la directiva NIS2 a la ley española: en qué punto estamos">
        <P>
          NIS2 (Directiva (UE) 2022/2555) es la sucesora de la primera
          directiva NIS y debía estar transpuesta al derecho nacional antes
          del <strong>17 de octubre de 2024</strong>. España no llegó a esa
          fecha. La transposición avanza a través del{' '}
          <strong>
            Anteproyecto de Ley de Coordinación y Gobernanza de la
            Ciberseguridad
          </strong>
          , aprobado por el Consejo de Ministros el 14 de enero de 2025, pero
          a mediados de 2026 el texto sigue en tramitación parlamentaria y
          todavía no se ha publicado en el BOE.
        </P>
        <P>
          Bruselas no lo ha dejado pasar: la Comisión Europea envió en mayo
          de 2025 un dictamen motivado y el 19 de mayo de 2026 un segundo
          requerimiento formal, exigiendo a España transponer la directiva
          &ldquo;con urgencia&rdquo;. La conclusión práctica es doble: la ley
          definitiva puede llegar en cualquier momento, y cuando llegue, es
          poco probable que incluya periodos de gracia generosos.
        </P>
        <P>
          Por eso, esperar a la publicación en el BOE es una mala estrategia.
          El contenido esencial de las obligaciones no lo decide la ley
          española: ya está escrito en la directiva, y el anteproyecto lo
          sigue de cerca. Quien empiece hoy con el análisis de riesgos, la
          detección y el registro de eventos llega preparado; quien espere,
          tendrá que correr.
        </P>
      </GuideSection>

      <GuideSection id="me-afecta" title="¿Me afecta NIS2?">
        <P>
          Como la directiva, el anteproyecto distingue dos categorías de
          entidades, con supervisión y sanciones diferentes:
        </P>
        <UL>
          <LI>
            <strong>Entidades esenciales</strong>: grandes organizaciones de
            sectores como energía, transporte, banca, infraestructuras de los
            mercados financieros, sanidad, agua potable y aguas residuales,
            infraestructura digital, administración pública y espacio.
          </LI>
          <LI>
            <strong>Entidades importantes</strong>: organizaciones medianas y
            grandes de sectores como servicios postales y de mensajería,
            gestión de residuos, química, alimentación, fabricación (incluidos
            productos sanitarios y electrónica), proveedores digitales e
            investigación.
          </LI>
        </UL>
        <P>
          El umbral de tamaño de referencia está en torno a los 50 empleados
          o 10 millones de euros de facturación anual. Organizaciones más
          pequeñas también pueden quedar incluidas, por ejemplo como único
          proveedor de un servicio esencial. Si tu organización opera en uno
          de estos sectores y supera el umbral, lo prudente es asumir que la
          ley te alcanzará y actuar en consecuencia.
        </P>
      </GuideSection>

      <GuideSection id="obligaciones" title="Las obligaciones: gestión de riesgos y responsabilidad de la dirección">
        <P>
          El núcleo del anteproyecto, igual que el de la directiva, son
          medidas técnicas y organizativas proporcionadas para gestionar los
          riesgos de tus redes y sistemas de información. La directiva
          menciona expresamente, entre otras:
        </P>
        <UL>
          <LI>análisis de riesgos y políticas de seguridad de la información;</LI>
          <LI>
            <strong>gestión de incidentes</strong>: detectar, analizar,
            contener y recuperar, y poder demostrarlo;
          </LI>
          <LI>continuidad de negocio, copias de seguridad y gestión de crisis;</LI>
          <LI>seguridad de la cadena de suministro;</LI>
          <LI>seguridad en el desarrollo, la adquisición y el mantenimiento de sistemas;</LI>
          <LI>procedimientos para medir la eficacia de las medidas;</LI>
          <LI>ciberhigiene y formación en seguridad;</LI>
          <LI>criptografía y, cuando proceda, cifrado;</LI>
          <LI>control de acceso, seguridad del personal y gestión de activos;</LI>
          <LI>autenticación multifactor y comunicaciones seguras.</LI>
        </UL>
        <P>
          Una de las grandes novedades es la{' '}
          <strong>responsabilidad al nivel de la dirección</strong>: los
          órganos de dirección deben aprobar las medidas, supervisar su
          aplicación y formarse en ciberseguridad. La seguridad deja de ser
          un asunto exclusivo del departamento de TI y pasa a ser una
          obligación de gobierno corporativo.
        </P>
        <P>
          En el plano institucional, el anteproyecto crea un{' '}
          <strong>Centro Nacional de Ciberseguridad</strong> como órgano de
          coordinación y reparte las competencias entre los ministerios del
          Interior, de Defensa (a través del CCN) y de Transformación
          Digital. Ese reparto puede ajustarse durante la tramitación; las
          obligaciones materiales para las entidades, no.
        </P>
      </GuideSection>

      <GuideSection id="notificacion-de-incidentes" title="La notificación de incidentes: 24 horas, 72 horas, un mes">
        <P>
          Los incidentes significativos deberán notificarse de forma
          escalonada, siguiendo el esquema que fija la propia directiva:
        </P>
        <OL>
          <LI>
            <strong>En 24 horas</strong>: una alerta temprana, con una primera
            valoración de si el incidente puede deberse a una acción ilícita o
            malintencionada y si puede tener efectos transfronterizos.
          </LI>
          <LI>
            <strong>En 72 horas</strong>: una notificación completa del
            incidente, con una evaluación inicial de gravedad e impacto y,
            cuando existan, indicadores de compromiso (IOC).
          </LI>
          <LI>
            <strong>En un mes</strong>: un informe final con la descripción
            detallada del incidente, la causa probable, las medidas adoptadas
            y el impacto transfronterizo.
          </LI>
        </OL>
        <P>
          Estos plazos son cortos. Quien empieza a recopilar logs después del
          incidente no llega: decir algo fundamentado en 24 horas sobre una
          posible acción malintencionada exige que los logs relevantes ya
          existan, sean consultables, y que la detección haya visto el
          incidente en primer lugar.
        </P>
      </GuideSection>

      <GuideSection id="supervision-y-sanciones" title="Supervisión y sanciones">
        <P>
          El anteproyecto prevé un régimen sancionador alineado con la
          directiva, con multas de hasta{' '}
          <strong>10 millones de euros</strong> para los incumplimientos más
          graves. A eso se suman las facultades de supervisión: las entidades
          esenciales estarán sujetas a supervisión proactiva (auditorías e
          inspecciones), las importantes a supervisión reactiva (tras
          indicios o incidentes).
        </P>
        <P>
          Igual de relevante que la multa es la responsabilidad personal: la
          dirección responde de aprobar y supervisar las medidas. Un
          expediente sancionador que concluye que la organización ni
          registraba ni detectaba lo que ocurría en sus sistemas es difícil
          de defender ante cualquier autoridad.
        </P>
      </GuideSection>

      <GuideSection id="logging-y-monitorizacion" title="Qué significa esto, en concreto, para el logging y la monitorización">
        <P>
          El logging y la monitorización aparecen detrás de casi todas las
          obligaciones: sin los logs adecuados no puedes detectar un
          incidente, ni fundamentar una alerta de 24 horas, ni escribir un
          informe final. Y esta es exactamente la parte que no depende de la
          tramitación en las Cortes: puedes construirla hoy. En la práctica:
        </P>
        <H3>Centraliza tus logs</H3>
        <P>
          Los logs de servidores, equipos de red, proveedores de identidad,
          servicios cloud y aplicaciones deben estar en un único sistema
          consultable. Reconstruir un incidente saltando entre diez sistemas
          sueltos no es viable en 72 horas.
        </P>
        <H3>Consérvalos el tiempo suficiente, de forma demostrable</H3>
        <P>
          La directiva no fija un plazo de retención concreto; el criterio es
          que puedas investigar y notificar incidentes. En la práctica lo
          habitual es entre 6 y 18 meses, con una capa caliente consultable y
          un archivo más económico. Documenta tu decisión y su justificación:
          es precisamente lo que preguntará una autoridad de supervisión.
        </P>
        <H3>Monitoriza en tiempo real, no a posteriori</H3>
        <P>
          Los plazos de notificación presuponen que ves los incidentes
          mientras ocurren. Eso exige reglas de detección sobre tus logs
          (intentos de acceso fallidos, comportamiento anómalo, conexiones de
          red sospechosas) y un sistema de alertas que llegue a tiempo a una
          persona, sin ahogarla en falsos positivos.
        </P>
        <H3>Mantén una pista de auditoría</H3>
        <P>
          Quién hizo qué y cuándo, también dentro de tu propia plataforma de
          logs. Una pista de auditoría a prueba de manipulaciones respalda
          tanto tus notificaciones como tu rendición de cuentas ante la
          autoridad.
        </P>
        <P>
          Seamos claros: ningún producto te hace conforme a NIS2 por sí solo.
          La conformidad exige análisis de riesgos, procesos y decisiones de
          dirección. Lo que LogPulse cubre es la parte de logging, detección
          y monitorización: gestión centralizada de logs con retención
          configurable, más de 50 detecciones integradas con mapeo a MITRE
          ATT&amp;CK, alertas basadas en riesgo que entregan un puñado de
          incidentes reales en lugar de miles de avisos, e informes que
          vinculan tus detecciones y datos con los requisitos de NIS2. Todos
          los datos permanecen en la UE (GCP Ámsterdam), lo que simplifica
          los acuerdos de tratamiento y la rendición de cuentas.
        </P>
      </GuideSection>

      <GuideSection id="paso-a-paso" title="Paso a paso: cómo empezar sin esperar al BOE">
        <OL>
          <LI>
            <strong>Determina si te afectará</strong>: sector, tamaño y papel
            en la cadena de suministro. En caso de duda, asume que sí.
          </LI>
          <LI>
            <strong>Implica a la dirección</strong>: la responsabilidad será
            suya y debe aprobar el enfoque desde el principio.
          </LI>
          <LI>
            <strong>Haz un análisis de riesgos</strong> e identifica qué
            sistemas y proveedores son críticos para tu servicio.
          </LI>
          <LI>
            <strong>Centraliza el logging y monta la detección</strong>: es el
            cimiento tanto de la gestión de riesgos como de la notificación.
          </LI>
          <LI>
            <strong>Ensaya el proceso de notificación</strong>: quién avisa en
            24 horas, a quién, y con qué información.
          </LI>
          <LI>
            <strong>Documéntalo todo</strong>: medidas, decisiones, plazos de
            retención y simulacros. La conformidad que no puedes demostrar no
            existe para una autoridad de supervisión.
          </LI>
        </OL>
        <P>
          ¿Quieres ver cómo LogPulse cubre la parte de logging, detección e
          informes? Consulta{' '}
          <GuideLink href="/compliance/nis2">conformidad NIS2 con LogPulse</GuideLink>{' '}
          y las capacidades de detección del{' '}
          <GuideLink href="/siem">SIEM de LogPulse</GuideLink>, o revisa los{' '}
          <GuideLink href="/pricing">planes y precios</GuideLink> y empieza
          gratis hoy mismo.
        </P>
      </GuideSection>
    </>
  );
}
